import {
  HHDivider,
  HHIconButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import Drawer from '@mui/material/Drawer';
import dayjs from 'dayjs';
import { FormEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MENU_WIDTH } from '../../../components/menu';
import { routes } from '../constants/strings/routes';
import {
  BatchClientInsurerType,
  ClientType,
  useGetClientInsurersByIdsQuery,
} from '../types';
import { selectSorterFunction } from '../utils/bills-utils';
import { BillingMenuLink } from './billing-menu-link';
import { BILL_DATE_FORMAT } from './bills/bill-grid-components/bill-management-types';
import { ClientInsurerSearch } from './client-insurers/client-insurer-search';
import { ClientSearch } from './clients/client-search';
import UserSearch from './user/user-search';

export const SUB_MENU_WIDTH = 250;

export const HOME_MENU_ITEM_TEST_ID = 'home-menu-item';
export const TOOL_MENU_ITEM_TEST_ID = 'tool-menu-item';
export const CLIENT_INSURERS_MENU_ITEM_TEST_ID = 'client-insurers-menu-item';
export const BILL_MANAGEMENT_MENU_ITEM_TEST_ID = 'bill-management-menu-item';
export const SUBMISSION_MANAGEMENT_MENU_ITEM_TEST_ID =
  'submission-management-menu-item';
export const UNBILLED_REPORT_MENU_ITEM_TEST_ID = 'unbilled-report-menu-item';
export const AGGREGATE_HOLDS_MENU_ITEM_TEST_ID = 'aggregate-holds-menu-item';
export const PROGRAM_CAPS_MENU_ITEM_TEST_ID = 'program-caps-menu-item';
export const FEE_SCHEDULES_MENU_ITEM_TEST_ID = 'fee-schedules-menu-item';
export const CPT_CODES_MENU_ITEM_TEST_ID = 'cpt-codes-menu-item';
export const ICD_CODES_MENU_ITEM_TEST_ID = 'icd-codes-menu-item';
export const ELIGIBILITY_VERIFIER_MENU_ITEM_TEST_ID =
  'eligibility-verifier-menu-item';

export const BillingSubMenu = (): JSX.Element => {
  const navigation = useNavigate();
  const [clientId, setClientId] = useState('');
  const [clientInsurerId, setClientInsurerId] = useState('');
  const isDisabled = !clientInsurerId || parseInt(clientInsurerId, 10) === 0;
  const [sortedClients, setSortedClients] = useState<ClientType[]>([]);
  const isClientDisabled = !clientId || parseInt(clientId, 10) === 0;
  const [sortedClientInsurers, setSortedClientInsurers] = useState<
    BatchClientInsurerType[]
  >([]);

  // Drawer controls
  const [isOpen, setIsOpen] = useState(false);

  const handleDrawerToggle = useCallback((): void => {
    setTimeout(() => {
      setIsOpen(prev => !prev);
    }, 100);
  }, []);

  const billingMenuLinks = [
    {
      name: 'Home',
      link: `${routes.billing.home}`,
      icon: AttachMoneyIcon,
      visible: () => true,
      dataTestId: HOME_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Tool',
      link: `${routes.billing.home}/tool`,
      icon: BuildCircleOutlinedIcon,
      visible: () => true,
      dataTestId: TOOL_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Client Insurers',
      link: `${routes.billing.main}`,
      icon: LanguageOutlined,
      visible: () => true,
      dataTestId: CLIENT_INSURERS_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Bill Management',
      link: `${routes.billing.home}${routes.billing.billManagement}?startDate=${dayjs().subtract(90, 'days').format(BILL_DATE_FORMAT)}`,
      icon: ManageSearchOutlinedIcon,
      visible: () => true,
      dataTestId: BILL_MANAGEMENT_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Submission Management',
      link: `${routes.billing.home}${routes.billing.submissionsManagement}?submissionStartDate=${dayjs().subtract(90, 'days').format(BILL_DATE_FORMAT)}`,
      icon: ManageSearchOutlinedIcon,
      visible: () => true,
      dataTestId: SUBMISSION_MANAGEMENT_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Unbilled Report',
      link: `${routes.billing.home}${routes.billing.unbilledReport}`,
      icon: ViewListIcon,
      visible: () => true,
      dataTestId: UNBILLED_REPORT_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Aggregate Holds',
      link: `${routes.billing.home}${routes.billing.holdsAggregate}`,
      icon: ViewListIcon,
      visible: () => true,
      dataTestId: AGGREGATE_HOLDS_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Program Caps',
      link: `${routes.billing.home}${routes.billing.programCaps}`,
      icon: LocalOfferOutlinedIcon,
      visible: () => true,
      dataTestId: PROGRAM_CAPS_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Fee Schedules',
      link: `${routes.billing.home}${routes.billing.clientFeeSchedules}`,
      icon: PriceCheckOutlinedIcon,
      visible: () => true,
      dataTestId: FEE_SCHEDULES_MENU_ITEM_TEST_ID,
    },
    {
      name: 'CPT Codes',
      link: `${routes.billing.home}${routes.billing.procedureCodes}`,
      icon: PinOutlinedIcon,
      visible: () => true,
      dataTestId: CPT_CODES_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Revenue Shares',
      link: `${routes.billing.home}${routes.billing.revenueShares}`,
      icon: PercentOutlinedIcon,
      visible: () => true,
      dataTestId: 'revenue-shares-menu-item',
    },
    {
      name: 'Volume Based Discounts',
      link: `${routes.billing.home}${routes.billing.volumeBasedDiscounts}`,
      icon: BlurLinearIcon,
      visible: () => true,
      dataTestId: 'volume-based-discounts-menu-item',
    },
    {
      name: 'ICD Codes',
      link: `${routes.billing.home}${routes.billing.icdCodes}`,
      icon: MedicalInformationOutlinedIcon,
      visible: () => false, // hidden until CRUD is complete
      dataTestId: ICD_CODES_MENU_ITEM_TEST_ID,
    },
    {
      name: 'Eligibility Verifier',
      link: `${routes.billing.home}${routes.billing.eligibilityVerifier}`,
      icon: FactCheckOutlinedIcon,
      visible: () => true, // migration-verification tool; reachable by direct URL
      dataTestId: ELIGIBILITY_VERIFIER_MENU_ITEM_TEST_ID,
    },
  ];

  const {
    data: clientInsurersData,
    error: clientInsurersError,
    loading: clientInsurersLoading,
  } = useGetClientInsurersByIdsQuery();

  const goToClientPage = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    e.preventDefault();
    if (!isClientDisabled) {
      setIsOpen(false);
      navigation(
        `${routes.billing.home}/${routes.billing.clientPath}/${clientId}`,
      );
    }
  };

  const goToInsurerPage = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ): void => {
    e.preventDefault();
    if (!isDisabled) {
      setIsOpen(false);
      navigation(`${routes.billing.main}/${clientInsurerId}`);
    }
  };

  useEffect(() => {
    if (clientInsurersData && !clientInsurersLoading) {
      const clientDataAlt = new Set(
        clientInsurersData?.getClientInsurersByIds.map(ci => ci.client),
      );
      setSortedClients(
        [...clientDataAlt].sort((a, b) =>
          selectSorterFunction(a.identifier, b.identifier),
        ),
      );
      setSortedClientInsurers(
        [...clientInsurersData.getClientInsurersByIds].sort((a, b) =>
          selectSorterFunction(a.client.identifier, b.client.identifier),
        ),
      );
    }
  }, [clientInsurersData, clientInsurersLoading]);

  return (
    <HHStack>
      <HHIconButton
        onClick={handleDrawerToggle}
        hhVariant="variant-bypass"
        sx={{ color: (theme): string => theme.palette.primary.dark }}
      >
        <MenuOutlinedIcon
          sx={{
            fontSize: 24,
            color: (theme): string => theme.palette.primary.dark,
          }}
        />
      </HHIconButton>
      <Drawer
        sx={{
          width: SUB_MENU_WIDTH,
          flexShrink: 0,
          gap: 2,
          '& .MuiDrawer-paper': {
            width: SUB_MENU_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: (theme): string => theme.palette.background.paper,
            marginLeft: `${MENU_WIDTH}px`,
            height: '100%',
            overflow: 'hidden', // Add this to prevent scroll jumps
          },
          '& .MuiDrawer-paperAnchorDockedLeft': {
            marginLeft: `${MENU_WIDTH}px`,
          },
        }}
        variant="temporary"
        anchor="left"
        open={isOpen}
        onClose={handleDrawerToggle}
      >
        <HHStack
          sx={{
            height: '100%',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <HHStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            padding={2}
          >
            <HHTypography
              hhVariant="section-title"
              color={(theme): string => theme.palette.primary.dark}
            >
              Billing
            </HHTypography>
            {isOpen && (
              <HHIconButton
                onClick={(): void => setIsOpen(false)}
                hhVariant="variant-bypass"
                sx={{
                  color: (theme): string => theme.palette.secondary.main,
                }}
              >
                <CloseOutlinedIcon />
              </HHIconButton>
            )}
          </HHStack>
          <HHDivider sx={{ margin: 0 }} />
          {billingMenuLinks
            .filter(link => link.visible())
            .map((link, idx) => (
              <>
                {idx !== 0 && <HHDivider sx={{ margin: 0 }} />}
                <BillingMenuLink
                  key={link.name}
                  name={link.name}
                  link={link.link}
                  Icon={link.icon}
                  onClick={handleDrawerToggle}
                  dataTestId={link.dataTestId}
                />
              </>
            ))}
          <HHDivider sx={{ margin: 0 }} />
          <ClientSearch
            sortedClients={sortedClients}
            clientInsurersLoading={clientInsurersLoading}
            clientInsurersError={clientInsurersError}
            handleSearch={goToClientPage}
            setClientId={setClientId}
            titleColor="primary.dark"
            isClientDisabled={isClientDisabled}
            compactMode
          />
          <HHDivider sx={{ margin: 0 }} />
          <ClientInsurerSearch
            sortedClientInsurers={sortedClientInsurers}
            clientInsurersLoading={clientInsurersLoading}
            clientInsurersError={clientInsurersError}
            handleSearch={goToInsurerPage}
            setClientInsurerId={setClientInsurerId}
            titleColor="primary.dark"
            isClientInsurerDisabled={isDisabled}
            compactMode
          />
          <HHDivider sx={{ margin: 0 }} />
          <UserSearch titleColor="primary.dark" compactMode />
        </HHStack>
      </Drawer>
    </HHStack>
  );
};
